// src/features/client_portfolios/new.tsx

import { Form } from "@/components/ui/form";
import { Card, CardBody, CardFooter, CardHeader, Divider } from "@heroui/react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import { z } from "zod";

import {
  AutocompleteAsyncField,
  DatePickerField,
  InputField,
} from "@/features/FormCard/Fields";

import { FormActionsNew } from "@/features/FormCard/Actions";
import {
  CancelConfirmationModal,
  ConfirmationModal,
} from "@/features/FormCard/Modals";

import { useEditForm } from "@/features/Edit/hooks";
import { NewDiff } from "@/features/FormCard/NewDiff";

import {
  dateSchema,
  parseToDateValue,
  preprocessTextAsyncAutocompleteField,
} from "@/features/FormCard/utils";

const client = "instit-quantilia";
const table_name = "client_portfolios";

const FormSchema = z.object({
  strategy_from_name: z.preprocess(
    (e: any) => preprocessTextAsyncAutocompleteField(e),
    z
      .union([
        z.string(),
        z.object({
          id: z.number(),
          name: z.string(),
        }),
      ])
      .optional(),
  ),
  strategy_from_id: z.coerce.number().optional().nullable(),

  strategy_to_name: z.preprocess(
    (e: any) => preprocessTextAsyncAutocompleteField(e),
    z
      .union([
        z.string(),
        z.object({
          id: z.number(),
          name: z.string(),
        }),
      ])
      .optional(),
  ),
  strategy_to_id: z.coerce.number().optional().nullable(),

  closing_date: z
    .preprocess((val) => parseToDateValue(val as string), dateSchema)
    .optional(),

  weight: z.coerce.number().optional().nullable(),
  market_value: z.coerce.number().optional().nullable(),
});

export function ClientPortfoliosNew() {
  const { t } = useTranslation();

  const navigate = useNavigate();

  const {
    form,
    handleReset,
    onSubmit,
    confirmUpdate,
    cancelUpdate,
    isConfirmationModalOpen,
    isDeleteModalOpen,
    openDeleteModal,
    closeDeleteModal,
    isUpdating,
    changedFields,
  } = useEditForm({
    client,
    table_name,
    FormSchema,
    isNew: true,
    disable_translations_request: true,
  });

  const navigateToList = () => navigate(-1);

  return (
    <div className="grid place-items-center h-full w-full">
      <div className="flex justify-center w-full max-w-card">
        <Card className="p-4 bg-white w-full">
          <Form {...form}>
            <form onSubmit={form.handleSubmit(onSubmit)}>
              <CardHeader className="text-lg font-bold text-gray-700">
                New Client Portfolio
              </CardHeader>
              <Divider />

              <CardBody className="flex flex-col gap-4">
                {/* Row 1: Strategy From & Strategy To */}
                <AutocompleteAsyncField
                  name="strategy_from_name"
                  label={t(`${table_name}.columns.strategy_from_name`, {
                    ns: "tables",
                    defaultValue: "Strategy From",
                  })}
                  base_url="etl-quantilia/strategies"
                  column_name="name"
                  id_column="strategy_from_id"
                  control={form.control}
                />
                <AutocompleteAsyncField
                  name="strategy_to_name"
                  label={t(`${table_name}.columns.name`, {
                    ns: "tables",
                    defaultValue: "Strategy To",
                  })}
                  base_url="etl-quantilia/strategies"
                  column_name="name"
                  id_column="strategy_to_id"
                  control={form.control}
                />

                {/* Row 2: Closing Date & Weight */}
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <DatePickerField
                    name="closing_date"
                    label={t(`${table_name}.columns.closing_date`, {
                      ns: "tables",
                      defaultValue: "Closing Date",
                    })}
                    control={form.control}
                  />
                  <InputField
                    name="weight"
                    label={t(`${table_name}.columns.weight`, {
                      ns: "tables",
                      defaultValue: "Weight",
                    })}
                    type="number"
                    control={form.control}
                  />
                </div>

                {/* Row 3: Market Value */}
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <InputField
                    name="market_value"
                    label={t(`${table_name}.columns.market_value`, {
                      ns: "tables",
                      defaultValue: "Market Value",
                    })}
                    type="number"
                    control={form.control}
                  />
                </div>
              </CardBody>

              <CardFooter>
                <FormActionsNew
                  onReset={handleReset}
                  onDelete={openDeleteModal}
                />
              </CardFooter>
            </form>
          </Form>

          <ConfirmationModal
            isOpen={isConfirmationModalOpen}
            isLoading={isUpdating}
            onClose={cancelUpdate}
            onConfirm={confirmUpdate}
            title={t("confirm_create")}
          >
            {changedFields
              ? NewDiff(changedFields, ["strategy_from_id", "strategy_to_id"])
              : "No changes detected"}
          </ConfirmationModal>

          <CancelConfirmationModal
            isOpen={isDeleteModalOpen}
            onClose={closeDeleteModal}
            onCancel={navigateToList}
          />
        </Card>
      </div>
    </div>
  );
}
